import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import {
  assistentes as api,
  marcas as apiMarcas,
  type Brevidade,
  type CaixaNaLista,
  type Formalidade,
  type PapelDoAssistente,
  type PedidoDeAssistente,
} from '../api/assistentes'
import { times as apiTimes, type TimeNaLista } from '../api/times'
import { Aviso } from '../componentes/Aviso'
import { Botao } from '../componentes/Botao'
import { Campo } from '../componentes/Campo'
import { Cartao } from '../componentes/Cartao'
import { Estado } from '../componentes/Estado'
import { mensagemDeErro } from './mensagemDeErro'
import './usuarios.css'
import './assistentes.css'

const BREVIDADES: Brevidade[] = ['Curta', 'Media', 'Detalhada']
const FORMALIDADES: Formalidade[] = ['Informal', 'Cordial', 'Formal']
const TIPOS_DE_CANAL = ['Telefone', 'Telegram', 'Instagram', 'Email', 'Site']

/**
 * O rótulo do canal que vai junto com a caixa nova.
 *
 * `Site` segue o que as caixas de site já gravadas trazem (demanda 291); os
 * demais ficam como sempre foram.
 */
export function canalExternoDe(tipo: string): string {
  if (tipo === 'Telefone') return ''
  if (tipo === 'Site') return 'Site'

  return `Channel::${tipo}`
}

/**
 * Criação de assistente.
 *
 * 🔴 Faltava — e não por esquecimento de tela: **não existia `POST` nenhum**. A
 * demanda 005 previa três assistentes em duas marcas e entregou só a edição. A
 * Giulia e a Letrinhas nasceram por `INSERT` no banco, que era a resposta que eu
 * tinha para dar quando o Ferrari perguntou como criar um, em 05/08.
 *
 * O **canal** é o campo que mais erra em silêncio: é por ele que a mensagem
 * recebida descobre a qual marca pertence. Errar o número não produz erro
 * nenhum — a mensagem é descartada, e a plataforma de atendimento registra
 * entrega bem-sucedida. Por isso a lista mostra **quem já ocupa cada canal**, e
 * por isso dá para criar o canal aqui mesmo, sem sair para outra tela e perder o
 * que já foi preenchido.
 */
export function AssistenteNovo({
  marca,
  aoFechar,
  aoCriar,
}: {
  marca: string
  aoFechar: () => void
  aoCriar: (id: number) => Promise<void>
}) {
  const { t } = useTranslation()

  const [caixas, setCaixas] = useState<CaixaNaLista[] | null>(null)
  const [timesDisponiveis, setTimes] = useState<TimeNaLista[]>([])
  const [erro, setErro] = useState<string | null>(null)
  const [enviando, setEnviando] = useState(false)
  const [novoCanal, setNovoCanal] = useState(false)

  const [dados, setDados] = useState<PedidoDeAssistente>({
    nome: '',
    caixasDeEntradaIds: [],
    papel: 'Atendimento',
    ativo: true,
    persona: '',
    formalidade: 'Cordial',
    brevidade: 'Curta',
    instrucoes: '',

    // Em branco de propósito: o servidor preenche com uma frase completa. O
    // aviso de automação é obrigação (`IA-02`), não preferência de quem cadastra.
    avisoDeAutomacao: '',
    setorDeEscalonamento: 'Comercial',

    // Nulo, e não string vazia: é o que faz o resumo do painel nascer com o
    // padrão de fábrica. Configurar destaque é ajuste fino, não cadastro.
    destaquesDoResumo: null,
  })

  const [canalNome, setCanalNome] = useState('')
  const [canalId, setCanalId] = useState('')
  const [canalTipo, setCanalTipo] = useState('Telefone')

  const carregarCaixas = useCallback(async () => {
    try {
      setCaixas(await apiMarcas.caixas(marca))

      // Time ausente não entra: oferecer um destino que sumiu do Chatwoot
      // é mandar a conversa para o vazio.
      apiTimes.listar().then((t) => setTimes(t.filter((x) => !x.ausente))).catch(() => setTimes([]))
    } catch {
      setCaixas([])
    }
  }, [marca])

  useEffect(() => {
    void carregarCaixas()
  }, [carregarCaixas])

  const alterar = <C extends keyof PedidoDeAssistente>(campo: C, valor: PedidoDeAssistente[C]) =>
    setDados({ ...dados, [campo]: valor })

  async function salvar(evento: FormEvent) {
    evento.preventDefault()

    setErro(null)
    setEnviando(true)

    try {
      const caixasDeEntradaIds = [...dados.caixasDeEntradaIds]

      if (novoCanal) {
        const criada = await apiMarcas.criarCaixa(
          marca,
          Number(canalId),
          canalNome,
          canalTipo,
          canalExternoDe(canalTipo),
        )

        caixasDeEntradaIds.push(criada.id)
      }

      const criado = await api.criar(marca, { ...dados, caixasDeEntradaIds })

      await aoCriar(criado.id)
    } catch (e) {
      setErro(mensagemDeErro(e, t))
    } finally {
      setEnviando(false)
    }
  }

  return (
    <main className="usuarios">
      <header className="usuarios__cabecalho">
        <div>
          <h1 className="usuarios__titulo">{t('assistentes.novo')}</h1>
          <p className="usuarios__descricao">{t('assistentes.novoDescricao', { marca })}</p>
        </div>

        <Botao variante="discreto" onClick={aoFechar}>
          {t('assistentes.voltar')}
        </Botao>
      </header>

      {erro && <Aviso tom="perigo">{erro}</Aviso>}

      <Cartao titulo={t('assistentes.identidade')}>
        <form onSubmit={salvar} noValidate>
          <Campo
            rotulo={t('assistentes.nome')}
            value={dados.nome}
            onChange={(e) => alterar('nome', e.target.value)}
            ajuda={t('assistentes.nomeAjuda')}
            obrigatorio
            autoFocus
          />

          <Campo
            rotulo={t('assistentes.persona')}
            value={dados.persona}
            onChange={(e) => alterar('persona', e.target.value)}
            ajuda={t('assistentes.personaAjuda')}
          />

          <fieldset className="usuarios__grupo">
            <legend>{t('assistentes.canal')}</legend>

            <p className="usuarios__descricao">{t('assistentes.canalAjuda')}</p>

            {caixas === null ? (
              <Estado tipo="carregando" />
            ) : (
              <>
                {/* Marcação múltipla, não lista suspensa: um assistente
                    atende vários canais, e caixa de seleção mostra de uma vez o
                    que está ligado e o que está ocupado por outro. */}
                {caixas.length === 0 ? (
                  <Estado tipo="vazio" titulo={t('assistentes.semCanalCadastrado')} />
                ) : (
                  <ul className="assistentes__canais">
                    {caixas.map((c) => {
                      const ocupado = c.assistente !== null
                      const ligado = dados.caixasDeEntradaIds.includes(c.id)

                      return (
                        <li key={c.id}>
                          <label className={ocupado ? 'assistentes__canal--ocupado' : undefined}>
                            <input
                              type="checkbox"
                              checked={ligado}
                              disabled={ocupado}
                              onChange={(e) =>
                                alterar(
                                  'caixasDeEntradaIds',
                                  e.target.checked
                                    ? [...dados.caixasDeEntradaIds, c.id]
                                    : dados.caixasDeEntradaIds.filter((id) => id !== c.id),
                                )
                              }
                            />
                            {c.nome} · {t('assistentes.inbox', { id: c.idExterno })}
                            {/* Canal ocupado aparece desabilitado, com o nome de
                                quem o ocupa: descobrir isso só no erro do
                                servidor é perder o formulário todo. */}
                            {ocupado && ` — ${t('assistentes.jaOcupado', { nome: c.assistente })}`}
                          </label>
                        </li>
                      )
                    })}
                  </ul>
                )}

                <label className="usuarios__filtro">
                  <input
                    type="checkbox"
                    checked={novoCanal}
                    onChange={(e) => setNovoCanal(e.target.checked)}
                  />
                  <span>{t('assistentes.criarCanal')}</span>
                </label>

                {novoCanal && (
                  <>
                    <Campo
                      rotulo={t('assistentes.canalNome')}
                      value={canalNome}
                      onChange={(e) => setCanalNome(e.target.value)}
                      obrigatorio
                    />

                    <Campo
                      rotulo={t('assistentes.canalId')}
                      type="number"
                      value={canalId}
                      onChange={(e) => setCanalId(e.target.value)}
                      ajuda={t('assistentes.canalIdAjuda')}
                      obrigatorio
                    />

                    <label className="usuarios__filtro">
                      <span>{t('assistentes.canalTipo')}</span>
                      <select value={canalTipo} onChange={(e) => setCanalTipo(e.target.value)}>
                        {TIPOS_DE_CANAL.map((tipo) => (
                          <option key={tipo} value={tipo}>
                            {t(`assistentes.tipo${tipo}`)}
                          </option>
                        ))}
                      </select>
                    </label>

                    {/* Onde a regra (s) do cliente nasce: é o canal que decide se
                        o telefone precisa ser pedido durante a conversa. */}
                    {canalTipo !== 'Telefone' && (
                      <Aviso tom="info">{t('assistentes.pedeTelefone')}</Aviso>
                    )}
                  </>
                )}
              </>
            )}
          </fieldset>

          <fieldset className="usuarios__grupo">
            <legend>{t('assistentes.brevidade')}</legend>
            {BREVIDADES.map((b) => (
              <label key={b} className="usuarios__opcao">
                <input
                  type="radio"
                  name="brevidade"
                  checked={dados.brevidade === b}
                  onChange={() => alterar('brevidade', b)}
                />
                <span>
                  <strong>{t(`assistentes.brevidade${b}`)}</strong>
                  <small>{t(`assistentes.brevidade${b}Ajuda`)}</small>
                </span>
              </label>
            ))}
          </fieldset>

          <fieldset className="usuarios__grupo">
            <legend>{t('assistentes.formalidade')}</legend>
            {FORMALIDADES.map((f) => (
              <label key={f} className="usuarios__opcao">
                <input
                  type="radio"
                  name="formalidade"
                  checked={dados.formalidade === f}
                  onChange={() => alterar('formalidade', f)}
                />
                <span>
                  <strong>{t(`assistentes.formalidade${f}`)}</strong>
                </span>
              </label>
            ))}
          </fieldset>

          {/* 🔴 Escolha na lista, não texto livre. Na validação de 06/08 o
              campo aceitou `jhjhjh` — um destino que não existe só aparece
              quando a conversa precisa dele, e aí ela fica parada num setor
              imaginário. */}
          <label className="usuarios__filtro">
            <span>{t('assistentes.setor')}</span>
            <select
              value={dados.setorDeEscalonamento ?? ''}
              onChange={(e) => alterar('setorDeEscalonamento', e.target.value || null)}
            >
              <option value="">{t('assistentes.semSetor')}</option>
              {timesDisponiveis.map((time) => (
                <option key={time.id} value={time.nome}>{time.nome}</option>
              ))}
            </select>
          </label>

          {timesDisponiveis.length === 0 && (
            <Aviso tom="alerta">{t('assistentes.semTimeSincronizado')}</Aviso>
          )}

          <label className="usuarios__filtro">
            <span>{t('assistentes.papel')}</span>
            <select
              value={dados.papel}
              onChange={(e) => alterar('papel', e.target.value as PapelDoAssistente)}
            >
              <option value="Atendimento">{t('assistentes.papelAtendimento')}</option>
              <option value="Interno">{t('assistentes.papelInterno')}</option>
              <option value="Copiloto">{t('assistentes.papelCopiloto')}</option>
            </select>
          </label>

          {dados.papel === 'Copiloto' && (
            <Aviso tom="alerta">{t('assistentes.papelCopilotoAviso')}</Aviso>
          )}

          <Aviso tom="info">{t('assistentes.avisoPadrao')}</Aviso>

          <div className="usuarios__acoes-formulario">
            <Botao type="submit" variante="primario" carregando={enviando}>
              {t('assistentes.criar')}
            </Botao>
          </div>
        </form>
      </Cartao>
    </main>
  )
}
